import { Controller } from '@hotwired/stimulus'
import {
  trans, OVERDUE, DUE_SOON, NEXT_EVENT_MARKER_AT, TIMING_ON_DAY, ADVANCE_EVENT_MARKER_FROM_TO, ADVANCE_EVENT_MARKER_TO
} from '../../translator'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import moment from 'moment-timezone'
import { getTimeFormat } from '../../utils/utils'
import escapeHtml from '../../utils/sanitize'

const DUE_SOON_WINDOW_SECONDS = 30 * 60
const SELECTABLE_WINDOW_HOURS = 29

/* global $ accountIsUseAmPm */

export default class extends Controller {
  static values = {
    logId: String,
    logName: String,
    eventMarkers: Array,
    timezone: String,
    numberOfDays: Number
  }

  static targets = [
    'select', 'selectWrapper', 'noEventMarkers', 'nextEventMarker', 'confirmModal', 'checklistsSection',
    'checklistsList', 'confirmAdvanceMessage', 'integrationsStartingSection', 'integrationsStartingList',
    'integrationsStoppingSection', 'integrationsStoppingList'
  ]

  connect () {
    if (this.eventMarkersValue.length === 0) {
      this.element.classList.add('d-none')
      return
    }

    this.filterDropdownOptions()
    this.previousValue = $(this.selectTarget).find(':selected').val()
    $(this.selectTarget).dropdown({
      onChange: (value) => {
        if (!this._updatingFromRemote && value) {
          this.showConfirmation(value)
        }
      }
    })
    this.selectTarget.style.visibility = ''
    this.updateNextDisplay(this.previousValue)
    this.handleMercureEvent = this.onMercureEvent.bind(this)
    document.addEventListener('MercureEvent', this.handleMercureEvent)
    $(this.confirmModalTarget).on('hidden.bs.modal.logEventMarker', () => {
      if (this._pendingEventMarkerId) {
        this.cancelAdvance()
      }
    })
  }

  disconnect () {
    clearTimeout(this._overdueTimer)
    document.removeEventListener('MercureEvent', this.handleMercureEvent)
    $(this.confirmModalTarget).off('hidden.bs.modal.logEventMarker')
  }

  onMercureEvent (mercureEvent) {
    const { eventType, event, logId, currentEventMarkerId, updatedEventMarkers } = mercureEvent.detail.data
    if (eventType !== 'logEvent' || event !== 'log.event_marker_updated') {
      return
    }
    if (logId !== this.logIdValue) {
      return
    }

    this.patchEventMarkers(updatedEventMarkers)
    this.setSelectedValue(currentEventMarkerId)

    this.previousValue = currentEventMarkerId
    this.filterDropdownOptions()
    this.updateNextDisplay(currentEventMarkerId)
  }

  filterDropdownOptions () {
    this.restoreDropdownOptions()

    const now = new Date()
    const timezone = this.timezoneValue
    const selectedValue = this.selectTarget.value
    const cutoffTimestamp = moment.tz(timezone).startOf('day').add(SELECTABLE_WINDOW_HOURS, 'hours').unix()

    const formatter = new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      numberingSystem: 'latn'
    })

    const getPart = (parts, type) => parts.find(part => part.type === type)?.value || ''
    const getDateKey = (date) => {
      const parts = formatter.formatToParts(date)
      return `${getPart(parts, 'year')}-${getPart(parts, 'month')}-${getPart(parts, 'day')}`
    }
    const getHourMinute = (date) => {
      const parts = formatter.formatToParts(date)
      return {
        hour: Number(getPart(parts, 'hour')),
        minute: Number(getPart(parts, 'minute'))
      }
    }

    const todayStr = getDateKey(now)
    const tomorrowStr = getDateKey(new Date(now.getTime() + 24 * 60 * 60 * 1000))

    if (selectedValue) {
      this.filterDropdownOptionsWithSelectedEventMarker({
        cutoffTimestamp,
        getDateKey,
        todayStr
      })
    } else {
      this.filterDropdownOptionsWithoutSelectedEventMarker({
        getDateKey,
        getHourMinute,
        todayStr,
        tomorrowStr
      })
    }

    const hasSelectableEventMarkers = Array.from(this.selectTarget.options).some(option => option.value)
    this.setTargetVisible(this.selectWrapperTarget, hasSelectableEventMarkers)
    this.setTargetVisible(this.noEventMarkersTarget, !hasSelectableEventMarkers)
  }

  restoreDropdownOptions () {
    const optionValues = new Set(Array.from(this.selectTarget.options).map(option => option.value))

    this.eventMarkersValue.forEach(eventMarker => {
      if (optionValues.has(eventMarker.id)) {
        return
      }

      const option = document.createElement('option')
      option.value = eventMarker.id
      option.textContent = eventMarker.name
      this.selectTarget.appendChild(option)
    })
  }

  filterDropdownOptionsWithSelectedEventMarker ({ cutoffTimestamp, getDateKey, todayStr }) {
    const selectedEventMarker = this.eventMarkersValue.find(eventMarker => eventMarker.id === this.selectTarget.value)

    for (const option of Array.from(this.selectTarget.options)) {
      if (!option.value) {
        continue
      }

      const eventMarker = this.eventMarkersValue.find(candidate => candidate.id === option.value)
      if (!eventMarker) {
        continue
      }

      if (selectedEventMarker && this.isEventMarkerBefore(eventMarker, selectedEventMarker)) {
        option.remove()
        continue
      }

      const eventMarkerTimestamp = Number(eventMarker.occurs_at)
      if (eventMarkerTimestamp > cutoffTimestamp) {
        option.remove()
        continue
      }

      const eventMarkerDate = new Date(eventMarkerTimestamp * 1000)
      const eventMarkerDateStr = getDateKey(eventMarkerDate)
      if (this.numberOfDaysValue > 0 && eventMarkerDateStr !== todayStr) {
        option.textContent = trans(TIMING_ON_DAY, {
          '%timing-name%': eventMarker.name,
          '%timing-day%': eventMarker.day + 1
        }, 'logs')
      } else {
        option.textContent = eventMarker.name
      }
    }
  }

  getEventMarkerSequenceKey (eventMarker) {
    return eventMarker.day * 10000 + eventMarker.occurs_at_in_minutes
  }

  isEventMarkerBefore (eventMarker, currentEventMarker) {
    return this.compareEventMarkers(eventMarker, currentEventMarker) < 0
  }

  compareEventMarkers (eventMarker, currentEventMarker) {
    const eventMarkerSequenceKey = this.getEventMarkerSequenceKey(eventMarker)
    const currentEventMarkerSequenceKey = this.getEventMarkerSequenceKey(currentEventMarker)

    if (eventMarkerSequenceKey !== currentEventMarkerSequenceKey) {
      return eventMarkerSequenceKey - currentEventMarkerSequenceKey
    }

    const orderComparison = Number(eventMarker.order ?? 0) - Number(currentEventMarker.order ?? 0)
    if (orderComparison !== 0) {
      return orderComparison
    }

    return Number(eventMarker.occurs_at) - Number(currentEventMarker.occurs_at)
  }

  // Which marker is soonest must key off occurs_at, not the scheduled sequence key:
  // a relative marker that shifts keeps its original occurs_at_in_minutes (only
  // occurs_at is updated), so the sequence key goes stale after a manual advance.
  // Saved order only breaks ties between markers occurring at the same time.
  compareEventMarkersByOccursAt (eventMarker, currentEventMarker) {
    const occursAtComparison = Number(eventMarker.occurs_at) - Number(currentEventMarker.occurs_at)
    if (occursAtComparison !== 0) {
      return occursAtComparison
    }

    return Number(eventMarker.order ?? 0) - Number(currentEventMarker.order ?? 0)
  }

  setSelectedValue (eventMarkerId) {
    this._updatingFromRemote = true
    if (eventMarkerId) {
      $(this.selectTarget).dropdown('set selected', eventMarkerId)
    } else {
      $(this.selectTarget).dropdown('clear')
    }
    this._updatingFromRemote = false
  }

  filterDropdownOptionsWithoutSelectedEventMarker ({ getDateKey, getHourMinute, todayStr, tomorrowStr }) {
    for (const option of Array.from(this.selectTarget.options)) {
      if (!option.value) {
        continue
      }

      const eventMarker = this.eventMarkersValue.find(candidate => candidate.id === option.value)
      if (!eventMarker) {
        continue
      }

      const eventMarkerTimestamp = Number(eventMarker.occurs_at)
      const eventMarkerDate = new Date(eventMarkerTimestamp * 1000)
      const eventMarkerDateStr = getDateKey(eventMarkerDate)

      if (eventMarkerDateStr === todayStr) {
        // today — keep as-is
      } else if (eventMarkerDateStr === tomorrowStr) {
        // tomorrow — show only if within the first 5 hours (before 05:00)
        const { hour, minute } = getHourMinute(eventMarkerDate)

        if (hour * 60 + minute < 5 * 60) {
          option.textContent = this.numberOfDaysValue > 0
            ? trans(TIMING_ON_DAY, {
              '%timing-name%': eventMarker.name,
              '%timing-day%': eventMarker.day + 1
            }, 'logs')
            : eventMarker.name
        } else {
          option.remove()
        }
      } else {
        option.remove()
      }
    }
  }

  showConfirmation (eventMarkerId) {
    this._pendingEventMarkerId = eventMarkerId
    const eventMarker = this.eventMarkersValue.find(candidate => candidate.id === eventMarkerId)
    const checklists = eventMarker?.pending_checklists ?? []
    const integrationsStarting = eventMarker?.integrations_starting ?? []
    const integrationsStopping = eventMarker?.integrations_stopping ?? []

    this.checklistsListTarget.innerHTML = ''
    if (checklists.length > 0) {
      checklists.forEach(name => {
        const li = document.createElement('li')
        li.textContent = name
        this.checklistsListTarget.appendChild(li)
      })
      this.setTargetVisible(this.checklistsSectionTarget, true)
    } else {
      this.setTargetVisible(this.checklistsSectionTarget, false)
    }

    this.integrationsStartingListTarget.innerHTML = ''
    if (integrationsStarting.length > 0) {
      integrationsStarting.forEach(name => {
        const li = document.createElement('li')
        li.textContent = name
        this.integrationsStartingListTarget.appendChild(li)
      })
      this.setTargetVisible(this.integrationsStartingSectionTarget, true)
    } else {
      this.setTargetVisible(this.integrationsStartingSectionTarget, false)
    }

    this.integrationsStoppingListTarget.innerHTML = ''
    if (integrationsStopping.length > 0) {
      integrationsStopping.forEach(name => {
        const li = document.createElement('li')
        li.textContent = name
        this.integrationsStoppingListTarget.appendChild(li)
      })
      this.setTargetVisible(this.integrationsStoppingSectionTarget, true)
    } else {
      this.setTargetVisible(this.integrationsStoppingSectionTarget, false)
    }

    // Update the confirmation message
    const messageParams = { '%log%': this.logNameValue, '%to%': eventMarker ? eventMarker.name : '' }
    this.setTargetVisible(this.confirmAdvanceMessageTarget, true)
    if (this.previousValue) {
      const previousEventMarker = this.eventMarkersValue.find(candidate => candidate.id === this.previousValue)
      messageParams['%from%'] = previousEventMarker ? previousEventMarker.name : ''
      this.confirmAdvanceMessageTarget.textContent = trans(ADVANCE_EVENT_MARKER_FROM_TO, messageParams, 'logs')
    } else {
      this.confirmAdvanceMessageTarget.textContent = trans(ADVANCE_EVENT_MARKER_TO, messageParams, 'logs')
    }

    $(this.confirmModalTarget).modal('show')
  }

  setTargetVisible (target, isVisible) {
    target.classList.toggle('d-none', !isVisible)
  }

  confirmAdvance () {
    $(this.confirmModalTarget).modal('hide')
    if (this._pendingEventMarkerId) {
      this.save(this._pendingEventMarkerId)
      this._pendingEventMarkerId = null
    }
  }

  cancelAdvance () {
    this._pendingEventMarkerId = null
    this.setSelectedValue(this.previousValue)
  }

  async save (eventMarkerId) {
    if (!eventMarkerId) {
      return
    }

    try {
      const response = await fetchWithHandlingLoginRedirect(
        '/internal/api/log/' + this.logIdValue + '/current-event-marker',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ event_marker_id: eventMarkerId })
        }
      )

      if (!response.ok) {
        this.setSelectedValue(this.previousValue)
      } else {
        const data = await response.json()
        const updatedEventMarkers = data?.result?.updated_event_markers ?? []
        this.patchEventMarkers(updatedEventMarkers)
        this.previousValue = eventMarkerId
        this.filterDropdownOptions()
        this.updateNextDisplay(eventMarkerId)
      }
    } catch (error) {
      this.setSelectedValue(this.previousValue)
    }
  }

  patchEventMarkers (updatedEventMarkers) {
    if (updatedEventMarkers?.length) {
      const patchMap = Object.fromEntries(updatedEventMarkers.map(em => [em.id, em.occurs_at]))
      this.eventMarkersValue = this.eventMarkersValue.map(em =>
        patchMap[em.id] !== undefined ? { ...em, occurs_at: patchMap[em.id] } : em
      )
    }
  }

  updateNextDisplay (eventMarkerId) {
    clearTimeout(this._overdueTimer)

    const eventMarkers = this.eventMarkersValue
    const current = eventMarkers.find(eventMarker => eventMarker.id === eventMarkerId)
    const startOfTodayTimestamp = moment.tz(this.timezoneValue).startOf('day').unix()
    const cutoffTimestamp = moment.tz(this.timezoneValue).startOf('day').add(SELECTABLE_WINDOW_HOURS, 'hours').unix()
    const isSelectableToday = (eventMarker) =>
      Number(eventMarker.occurs_at) >= startOfTodayTimestamp && Number(eventMarker.occurs_at) <= cutoffTimestamp
    const nextCandidates = current
      ? eventMarkers.filter(eventMarker => eventMarker.id !== current.id && !this.isEventMarkerBefore(eventMarker, current))
      : eventMarkers.filter(isSelectableToday)

    const next = nextCandidates
      .sort((a, b) => this.compareEventMarkersByOccursAt(a, b))[0]

    const container = this.nextEventMarkerTarget
    container.innerHTML = ''

    if (!next) {
      // Keep the container visible (empty) so its reserved min-height in the
      // SCSS keeps the dropdown + heading aligned with sibling cards that have
      // a next-marker line.
      this.element.classList.remove('is-overdue', 'is-due-soon')
      this.dispatchState(eventMarkerId, null)
      return
    }

    const nowTs = Math.floor(Date.now() / 1000)
    const nextTs = Number(next.occurs_at)
    const dueSoonAt = nextTs - DUE_SOON_WINDOW_SECONDS
    const isOverdue = nextTs < nowTs
    const isDueSoon = !isOverdue && nowTs >= dueSoonAt

    const time = moment.unix(nextTs)
      .tz(this.timezoneValue)
      .format(getTimeFormat(accountIsUseAmPm))

    // Mirror the state on the card root so the border can tint to match the dot.
    this.element.classList.toggle('is-overdue', isOverdue)
    this.element.classList.toggle('is-due-soon', isDueSoon)

    // Always render a dot so all "Next:" lines align horizontally — the on-track
    // case uses a neutral slate. Colours are picked up from the .dot rule in
    // event-markers.scss to match the panel-header summary palette.
    const dot = document.createElement('span')
    dot.className = 'dot'
    if (isOverdue) {
      dot.classList.add('is-overdue')
      dot.title = trans(OVERDUE, {}, 'logs')
    } else if (isDueSoon) {
      dot.classList.add('is-due-soon')
      dot.title = trans(DUE_SOON, {}, 'logs')
    }
    container.appendChild(dot)

    if (!isOverdue) {
      // Schedule a re-render at the next bucket boundary: on-track → due-soon
      // (at dueSoonAt) or due-soon → overdue (at nextTs).
      const transitionAt = isDueSoon ? nextTs : dueSoonAt
      this._overdueTimer = setTimeout(() => {
        this._overdueTimer = null
        this.updateNextDisplay(eventMarkerId)
      }, Math.max(0, (transitionAt - nowTs) * 1000))
    }

    const nextTimingName = current && next.day !== current.day
      ? trans(TIMING_ON_DAY, {
        '%timing-name%': next.name,
        '%timing-day%': next.day + 1
      }, 'logs')
      : next.name

    // Translation contains <strong> tags so the prefix and time render bold while
    // the marker name stays normal weight. Escape user-supplied values before
    // substituting so they can't break out into HTML.
    const labelHTML = trans(NEXT_EVENT_MARKER_AT, {
      '%timing-name%': escapeHtml(nextTimingName),
      '%time%': escapeHtml(time)
    }, 'logs')
    const labelPlainText = labelHTML.replace(/<[^>]*>/g, '')

    const action = document.createElement('button')
    action.type = 'button'
    action.className = 'event-marker-next-action btn btn-link btn-sm p-0 border-0 text-left'
    action.title = labelPlainText
    const small = document.createElement('small')
    small.innerHTML = labelHTML
    action.appendChild(small)
    action.addEventListener('click', () => this.showConfirmation(next.id))
    container.appendChild(action)

    this.dispatchState(eventMarkerId, nextTs)
  }

  dispatchState (currentMarkerId, nextOccursAt) {
    this.dispatch('state', {
      detail: {
        logId: this.logIdValue,
        hasCurrent: !!currentMarkerId,
        nextOccursAt
      }
    })
  }
}
